import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Mail, Calendar, User, BookOpen, Star } from 'lucide-react';
import Shell from '../components/TopBarShell';
import { useT, useLocale } from '../contexts/LocaleProvider';
import { iconFor, LT } from '../contexts/AppCatalogueContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { localizedApp } from '../lib/appLocale';
import Markdown from '../lib/miniMarkdown';
import api from '../utils/api';

/* Per-app detail page · author, released, contact, doc link, big
 * description and the open-the-app CTA. */
export default function AppDetail() {
  const { slug } = useParams();
  const t = useT();
  const { intlLocale, locale } = useLocale();
  const { favorites, toggle } = useFavorites();
  const [app, setApp] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.app(slug)
      .then(setApp)
      .catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <Shell>
        <div className="max-w-[600px] mt-10 rounded-2xl p-6"
             style={{ background: LT.beigeLight, border: `1px dashed ${LT.green30}`, color: LT.green50 }}>
          {t('apps.notFound')} · <Link to="/apps" style={{ color: LT.greenDeep }} className="underline">{t('apps.backToCatalogue')}</Link>
        </div>
      </Shell>
    );
  }
  if (!app) {
    return (
      <Shell>
        <div className="mt-10 text-[13px]" style={{ color: LT.green50 }}>{t('common.loading')}</div>
      </Shell>
    );
  }

  const Icon = iconFor(app.icon_name);
  const isFav = favorites.includes(app.slug);
  const isInternal = !!app.internal_route;
  const { name: localName, description: localDescription } = localizedApp(app, locale);
  const released = app.released_at
    ? new Intl.DateTimeFormat(intlLocale, { year: 'numeric', month: 'long' }).format(new Date(app.released_at))
    : null;

  return (
    <Shell>
      <Link to="/apps"
            className="inline-flex items-center gap-1.5 text-[13px] mb-4"
            style={{ color: LT.green50 }}>
        <ArrowLeft className="h-3.5 w-3.5" /> {t('apps.backToCatalogue')}
      </Link>

      <header className="flex items-start gap-5 max-w-[820px]">
        <div className="h-16 w-16 rounded-2xl flex items-center justify-center shrink-0 overflow-hidden"
             style={{ background: LT.beigeLight, border: `1px solid ${app.dot_color}30` }}>
          {app.logo
            ? <img src={app.logo} alt="" className="h-full w-full object-contain p-1.5" />
            : <Icon className="h-7 w-7" style={{ color: app.dot_color }} />}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-[2rem] leading-tight tracking-[-0.02em]"
              style={{ color: LT.greenDeep }}>
            {localName}
          </h1>
          <div className="mt-1 flex items-center gap-3 flex-wrap text-[12.5px]" style={{ color: LT.green50 }}>
            {(() => {
              // Only show the group eyebrow when we actually have a
              // translation for it · otherwise t() returns the raw key
              // (e.g. "splash.group.other.eyebrow") which leaked into
              // the UI for any unknown group.
              const key = `splash.group.${app.group}.eyebrow`;
              const label = t(key);
              return label && label !== key
                ? <span className="uppercase tracking-[0.14em] font-medium">{label}</span>
                : null;
            })()}
            {app.is_featured && (
              <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.12em]"
                    style={{ background: LT.yellowLight, color: LT.greenDeep }}>
                {t('apps.featured')}
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => toggle(app.slug)}
          className="p-2 rounded-md transition hover:bg-black/[0.04]"
          aria-label={isFav ? t('splash.fav.unpin') : t('splash.fav.pin')}
        >
          <Star className="h-5 w-5" style={{ color: LT.greenDeep, fill: isFav ? LT.yellowLight : 'transparent' }} />
        </button>
      </header>

      <section className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6 max-w-[1100px]">
        <article className="lg:col-span-8">
          <div className="rounded-2xl p-6"
               style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
            {localDescription && (
              <>
                <div className="text-[11px] uppercase tracking-[0.14em] font-medium" style={{ color: LT.green50 }}>
                  {t('apps.about')}
                </div>
                <div className="mt-2 text-[15px] leading-relaxed" style={{ color: '#3a4f44' }}>
                  <Markdown source={localDescription} />
                </div>
              </>
            )}
            <div className={`${localDescription ? 'mt-6 pt-4 border-t' : ''} flex items-center gap-2 flex-wrap`}
                 style={{ borderColor: LT.green30 }}>
              {isInternal ? (
                <Link to={app.internal_route}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13.5px]"
                      style={{ background: LT.greenDeep, color: LT.beigeLight }}>
                  {t('apps.open')}
                </Link>
              ) : app.href && (
                <a href={app.href} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13.5px]"
                   style={{ background: LT.greenDeep, color: LT.beigeLight }}>
                  {t('apps.open')} <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
              {app.doc_url && (
                <a href={app.doc_url} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13.5px] transition hover:bg-black/[0.04]"
                   style={{ color: LT.greenDeep, border: `1px solid ${LT.green30}` }}>
                  <BookOpen className="h-3.5 w-3.5" /> {t('apps.documentation')}
                </a>
              )}
            </div>
          </div>
        </article>

        <aside className="lg:col-span-4 space-y-3">
          <Meta icon={User}     label={t('apps.owner')}    value={app.owner_email}    href={app.owner_email ? `mailto:${app.owner_email}` : null} />
          <Meta icon={Mail}     label={t('apps.contact')}  value={app.contact_email}  href={app.contact_email ? `mailto:${app.contact_email}` : null} />
          <Meta icon={Calendar} label={t('apps.released')} value={released} />
        </aside>
      </section>
    </Shell>
  );
}

const Meta = ({ icon: Icon, label, value, href }) => {
  if (!value) return null;
  return (
    <div className="rounded-xl px-4 py-3"
         style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] font-medium"
           style={{ color: LT.green50 }}>
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div className="mt-1 text-[14px]" style={{ color: LT.greenDeep }}>
        {href ? <a href={href} className="hover:underline">{value}</a> : value}
      </div>
    </div>
  );
};
